import React, { FC, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { isAuthCookieAvailable, requestStatus } from '@clatter/platform';
import { config } from '../../config';
import { CircularLoader } from '../../shared';
import { authSelectors, checkSession } from '../../store';

interface ProtectedRouteProps {
  element: ReactNode;
}

const StyledContainer = styled.div`
  min-width: 100vw;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// @ts-ignore
const ProtectedRoute: FC<ProtectedRouteProps> = ({ element }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const authLoadingStatus = useSelector(authSelectors.loadingStatus);

  useEffect(() => {
    let didCall = false;

    const verifyAuth = async () => {
      didCall = true;
      await dispatch(checkSession());
    };

    if (authLoadingStatus === requestStatus.initial && !isAuthenticated && !didCall) {
      verifyAuth();
    }
  }, [isAuthenticated, authLoadingStatus]);

  useEffect(() => {
    // this cookie is used to track login state between apps
    if (!isAuthCookieAvailable()) {
      navigate('/logout', { state: { redirectTo: window.location.origin + location.pathname + location.search } });
    }
  }, [navigate, location.pathname, location.search]);

  if (authLoadingStatus === requestStatus.pending || authLoadingStatus === requestStatus.initial) {
    return (
      <StyledContainer>
        <CircularLoader />
      </StyledContainer>
    );
  }

  if (!isAuthenticated) {
    const loginUrl = config.auth.loginUrl;

    if (loginUrl) {
      const origin = window.location.origin;
      const redirectTo = `${origin}${location.pathname}${location.search}`;

      window.location.replace(`${loginUrl}?redirectTo=${redirectTo}`);

      return null;
    }

    console.error("Couldn't redirect to login: no loginUrl provided!");

    return (
      <StyledContainer>
        <h4>Please log in...</h4>
      </StyledContainer>
    );
  }

  return element;
};

export default ProtectedRoute;
